import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Loader2, Sparkles } from 'lucide-react'
import { ProfilePictureUploadGeneric } from '@/components/profile/profile-picture-upload-generic'
import { AIAssistantDialog } from '@/components/ai/ai-assistant-dialog'
import { useAIWritingAssistant } from '@/lib/hooks/use-ai-writing-assistant'
import { getVideoEmbedInfo } from '@/lib/utils/video'

interface FreelancerBasicInfoFormProps {
  user: ReturnType<typeof useUser>['user']
  displayName: string
  headline: string
  freelancerBio: string
  freelancerEmail: string
  profilePictureUrl: string | null
  location: string
  videoIntroUrl: string
  onHeadlineChange: (value: string) => void
  onFreelancerBioChange: (value: string) => void
  onLocationChange: (value: string) => void
  onVideoIntroUrlChange: (value: string) => void
  onImageUploaded: (imageUrl: string) => void
  onImageRemoved: () => void
}

export function FreelancerBasicInfoForm({
  user,
  displayName,
  headline,
  freelancerBio,
  freelancerEmail,
  profilePictureUrl,
  location,
  videoIntroUrl,
  onHeadlineChange,
  onFreelancerBioChange,
  onLocationChange,
  onVideoIntroUrlChange,
  onImageUploaded,
  onImageRemoved,
}: FreelancerBasicInfoFormProps) {
  const { generateHeadline, generateBio, isLoading: isAIGenerating, generatingType, data: aiGeneratedData, reset: resetAIData } = useAIWritingAssistant();
  const [isAIHeadlineDialogOpen, setIsAIHeadlineDialogOpen] = useState(false);
  const [isAIBioDialogOpen, setIsAIBioDialogOpen] = useState(false);

  const handleGenerateHeadline = async (currentText: string | undefined, options: any) => {
    return await generateHeadline(currentText ?? headline, options);
  };

  const handleAcceptHeadline = (text: string) => {
    onHeadlineChange(text);
    resetAIData();
  };

  const handleGenerateBio = async (currentText: string | undefined, options: any) => {
    return await generateBio(currentText ?? freelancerBio, options);
  };

  const handleAcceptBio = (text: string) => {
    onFreelancerBioChange(text);
    resetAIData();
  };

  return (
    <>
      <div className="flex justify-center pb-2">
        <ProfilePictureUploadGeneric
          profileType="freelancer"
          profileId={null}
          currentUrl={profilePictureUrl || null}
          name={displayName || 'Your Name'}
          onImageUploaded={onImageUploaded}
          onImageRemoved={onImageRemoved}
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          First Name <span className="text-red-500">*</span>
        </label>
        <Input
          value={user?.firstName || ''}
          readOnly
          disabled
          className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Last Name <span className="text-red-500">*</span>
        </label>
        <Input
          value={user?.lastName || ''}
          readOnly
          disabled
          className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Email <span className="text-red-500">*</span>
        </label>
        <Input
          type="email"
          value={freelancerEmail}
          readOnly
          disabled
          className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Location <span className="text-red-500">*</span>
        </label>
        <Input
          value={location}
          onChange={(e) => onLocationChange(e.target.value)}
          placeholder="City, Country"
          className="h-12 rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
          required
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-slate-700">
            Headline <span className="text-red-500">*</span>
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsAIHeadlineDialogOpen(true)}
            disabled={isAIGenerating}
            className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
          >
            {isAIGenerating && generatingType === 'headline' ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3 mr-1" />
                Generate headline with AI
              </>
            )}
          </Button>
        </div>
        <AIAssistantDialog
          open={isAIHeadlineDialogOpen}
          onOpenChange={(open) => {
            setIsAIHeadlineDialogOpen(open);
            if (!open) {
              resetAIData();
            }
          }}
          onGenerate={handleGenerateHeadline}
          onAccept={handleAcceptHeadline}
          generatedText={aiGeneratedData?.type === 'headline' ? aiGeneratedData.text : null}
          isLoading={isAIGenerating && generatingType === 'headline'}
          type="headline"
          currentText={headline}
        />
        <Input
          value={headline}
          maxLength={100}
          onChange={(e) => onHeadlineChange(e.target.value)}
          placeholder="AI/ML Specialist, Data Scientist, etc."
          className="h-12 rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
          required
          autoFocus
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-slate-700">
            Bio <span className="text-red-500">*</span>
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsAIBioDialogOpen(true)}
            disabled={isAIGenerating}
            className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
          >
            {isAIGenerating && generatingType === 'bio' ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3 mr-1" />
                Write bio with AI
              </>
            )}
          </Button>
        </div>
        <AIAssistantDialog
          open={isAIBioDialogOpen}
          onOpenChange={(open) => {
            setIsAIBioDialogOpen(open);
            if (!open) {
              resetAIData();
            }
          }}
          onGenerate={handleGenerateBio}
          onAccept={handleAcceptBio}
          generatedText={aiGeneratedData?.type === 'bio' ? aiGeneratedData.text : null}
          isLoading={isAIGenerating && generatingType === 'bio'}
          type="bio"
          currentText={freelancerBio}
        />
        <Textarea
          value={freelancerBio}
          onChange={(e) => onFreelancerBioChange(e.target.value)}
          placeholder="Tell us about yourself..."
          className="min-h-[100px] rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0 resize-none"
          required
          maxLength={1500}
        />
        <p className="text-xs mt-1">
          {freelancerBio.length < 100 ? (
            <span className="text-amber-500">Characters should be between 100 and 1500 ({freelancerBio.length}/1500)</span>
          ) : (
            <span className="text-slate-500">{freelancerBio.length}/1500 characters</span>
          )}
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Video Introduction <span className="text-slate-400 font-normal">(Optional)</span>
        </label>
        <Input
          value={videoIntroUrl}
          onChange={(e) => onVideoIntroUrlChange(e.target.value)}
          placeholder="https://www.loom.com/share/... or YouTube/Vimeo URL"
          className="h-12 rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
        />
        <p className="text-xs text-slate-500 mt-1">
          Paste a YouTube, Vimeo, or Loom URL introducing yourself (can be added later)
        </p>
        {videoIntroUrl.trim() && !getVideoEmbedInfo(videoIntroUrl).type && (
          <p className="text-xs text-red-500 mt-1">
            Please enter a valid YouTube, Vimeo, or Loom URL
          </p>
        )}
      </div>
    </>
  )
}
